import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LogOut, RefreshCw, PanelRight, User as UserIcon, FileText } from 'lucide-react';

import InboxKpiStrip from '@/components/OfficerInbox/InboxKpiStrip';
import InboxFilterBar from '@/components/OfficerInbox/InboxFilterBar';
import InboxTable from '@/components/OfficerInbox/InboxTable';
import ProductivityRail from '@/components/OfficerInbox/ProductivityRail';
import DemoResetButton from '@/components/DemoResetButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'awaiting_score', label: 'Awaiting Score', testid: 'tab-awaiting-score' },
  { key: 'pending_review', label: 'Pending Review', testid: 'tab-pending-review' },
  { key: 'recently_decided', label: 'Recent Disposals', testid: 'tab-recently-decided' },
];

const dateRangeToIso = (range) => {
  if (!range || range === 'all') return { date_from: null, date_to: null };
  const now = new Date();
  const to = now.toISOString();
  let from;
  if (range === 'today') {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    from = d.toISOString();
  } else if (range === '7d') {
    const d = new Date(Date.now() - 7 * 86400000);
    from = d.toISOString();
  } else if (range === '30d') {
    const d = new Date(Date.now() - 30 * 86400000);
    from = d.toISOString();
  }
  return { date_from: from, date_to: to };
};

const OfficerInbox = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending_review');
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [productivity, setProductivity] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [filters, setFilters] = useState({ q: '', district: null, journey_type: null, status: null, date_range: 'all' });
  const [loading, setLoading] = useState(true);
  const [railOpen, setRailOpen] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const loadFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/filters`);
      setDistricts(res.data.districts || []);
    } catch { /* ignore */ }
  }, []);

  const loadKpis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/officer/kpis`);
      setKpis(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const { date_from, date_to } = dateRangeToIso(filters.date_range);
      const params = {
        tab,
        district: filters.district || undefined,
        journey_type: filters.journey_type || undefined,
        status: filters.status || undefined,
        q: filters.q || undefined,
        date_from: date_from || undefined,
        date_to: date_to || undefined,
        limit: pageSize,
        offset: page * pageSize,
      };
      const res = await axios.get(`${API}/dashboard/officer/inbox`, { params });
      setRows(res.data.cases || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [tab, filters, page, pageSize]);

  const loadProductivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/officer/productivity?window=30d`);
      setProductivity(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadKpis(); loadProductivity(); }, [loadKpis, loadProductivity]);
  useEffect(() => { loadInbox(); }, [loadInbox]);

  // Reset to first page when tab/filters change
  useEffect(() => { setPage(0); }, [tab, filters]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(() => {
      loadKpis();
      loadInbox();
      loadProductivity();
    }, 60000);
    return () => clearInterval(id);
  }, [loadKpis, loadInbox, loadProductivity]);

  const handleRefresh = () => {
    loadKpis();
    loadInbox();
    loadProductivity();
    toast.success('Inbox refreshed');
  };

  const handleClearFilters = () => {
    setFilters({ q: '', district: null, journey_type: null, status: null, date_range: 'all' });
  };

  const handleHeatmapClick = ({ district, relationship }) => {
    // Legacy journey_type filter: Existing maps to a composite (we only filter by
    // one value at API level, default to SAME_LIMIT_RENEWAL and let user widen);
    // chairman wants merged UI, backend still stores the specific type.
    let journey_type = null;
    if (relationship === 'New') journey_type = 'NEW_FARMER';
    // For "Existing" we leave journey_type unset so both Renewal and Enhanced match
    setFilters(prev => ({ ...prev, district, journey_type }));
    toast.info(`Filtered: ${district} · ${relationship}`);
  };

  const tabCount = (key) => {
    if (!kpis) return null;
    if (key === 'awaiting_score') return kpis.awaiting_score;
    if (key === 'pending_review') return kpis.pending_review;
    return null;
  };

  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      {/* Top bar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '30px' }} />
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-none" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Officer Inbox
                </h1>
                <div className="text-[10px] text-gray-500 mt-0.5">Score, review, and decide pending assessments · IST · {todayLabel}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded text-[11px]">
                <UserIcon className="h-3 w-3 text-slate-600" />
                <span className="text-slate-700 font-medium">{user?.email}</span>
                <Badge className="text-[9px] border-none bg-blue-100 text-blue-800">{user?.role}</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-8" data-testid="inbox-refresh-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
              <DemoResetButton role={user?.role} onDone={handleRefresh} />
              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/demo-script.pdf`, { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'SaakhSetu_Demo_Script.pdf';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch {
                    toast.error('Failed to download demo script');
                  }
                }}
                data-testid="demo-script-btn"
              >
                <FileText className="h-3.5 w-3.5 mr-1" /> Demo Script
              </Button>
              <Button variant="ghost" size="sm" className="h-8 xl:hidden" onClick={() => setRailOpen(v => !v)} data-testid="inbox-toggle-rail-btn">
                <PanelRight className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={onLogout} className="h-8" data-testid="inbox-logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-5">
        <InboxKpiStrip data={kpis} loading={!kpis} />

        <div className="flex flex-col xl:flex-row gap-5">
          {/* Center column */}
          <div className="flex-1 min-w-0">
            <InboxFilterBar
              districts={districts}
              filters={filters}
              onChange={setFilters}
              onClear={handleClearFilters}
              tab={tab}
            />

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
              {TABS.map(t => {
                const active = tab === t.key;
                const count = tabCount(t.key);
                return (
                  <button
                    key={t.key}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${active ? 'text-blue-700' : 'text-gray-500 hover:text-gray-800'}`}
                    onClick={() => setTab(t.key)}
                    data-testid={t.testid}
                  >
                    {t.label}
                    {count != null && (
                      <Badge className="ml-2 text-[10px] border-none bg-slate-100 text-slate-700 tabular-nums">{count}</Badge>
                    )}
                    {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-700" />}
                  </button>
                );
              })}
              <div className="ml-auto text-[11px] text-gray-400 pr-2 self-center" data-testid="inbox-total-count">
                {totalCount} {totalCount === 1 ? 'case' : 'cases'}
              </div>
            </div>

            <InboxTable rows={rows} tab={tab} loading={loading} />

            {/* Pagination */}
            {totalCount > pageSize && (
              <div className="flex items-center justify-between mt-3 text-[11px] text-gray-600" data-testid="inbox-pagination">
                <div>
                  Showing <span className="font-semibold">{page * pageSize + 1}</span>–<span className="font-semibold">{Math.min((page + 1) * pageSize, totalCount)}</span> of <span className="font-semibold">{totalCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[11px]"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0 || loading}
                    data-testid="inbox-prev-page-btn"
                  >Previous</Button>
                  <div className="text-[11px] text-gray-500">
                    Page <span className="font-semibold text-gray-900">{page + 1}</span> / {Math.max(1, Math.ceil(totalCount / pageSize))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[11px]"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * pageSize >= totalCount || loading}
                    data-testid="inbox-next-page-btn"
                  >Next</Button>
                </div>
              </div>
            )}
          </div>

          {/* Right rail */}
          {railOpen && (
            <ProductivityRail
              data={productivity}
              loading={!productivity}
              amounts={kpis?.amounts}
              onHeatmapClick={handleHeatmapClick}
              onClose={() => setRailOpen(false)}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default OfficerInbox;
